import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input"]

  connect() {
    this.results = []
    this.activeIndex = -1
    this.menu = document.createElement("div")
    this.menu.hidden = true
    this.menu.setAttribute("role", "listbox")
    this.menu.setAttribute("aria-label", "People and work references")
    this.menu.className = "absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-mod-border bg-white dark:bg-[#231f1b] shadow-xl"
    this.inputTarget.parentElement.classList.add("relative")
    this.inputTarget.parentElement.appendChild(this.menu)
  }

  disconnect() {
    this.menu?.remove()
  }

  async search() {
    const match = this.currentMatch()
    if (!match) return this.close()

    const response = await fetch(`/api/v1/mentions/users?q=${encodeURIComponent(match.query)}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    if (!response.ok) return this.close()
    const payload = await response.json()
    this.results = [
      ...(payload.users || []).map((user) => ({ ...user, type: "user", title: user.name, subtitle: `@${user.handle}` })),
      ...(payload.references || [])
    ]
    this.activeIndex = this.results.length ? 0 : -1
    this.render()
  }

  navigate(event) {
    if (this.menu.hidden || !this.results.length) return
    if (event.key === "Escape") {
      event.preventDefault()
      return this.close()
    }
    if (!["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(event.key)) return
    event.preventDefault()
    if (event.key === "ArrowDown") this.activeIndex = (this.activeIndex + 1) % this.results.length
    if (event.key === "ArrowUp") this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length
    if (event.key === "Enter" || event.key === "Tab") return this.choose(this.results[this.activeIndex])
    this.render()
  }

  choose(item) {
    const match = this.currentMatch()
    if (!match || !item) return
    const value = this.inputTarget.value
    const replacement = item.type === "user" ? `@${item.handle} ` : `${item.token} `
    this.inputTarget.value = `${value.slice(0, match.start)}${replacement}${value.slice(match.end)}`
    const cursor = match.start + replacement.length
    this.inputTarget.setSelectionRange(cursor, cursor)
    this.inputTarget.focus()
    this.close()
  }

  currentMatch() {
    const cursor = this.inputTarget.selectionStart
    const before = this.inputTarget.value.slice(0, cursor)
    const match = before.match(/(?:^|\s)@([A-Za-z0-9._-]{0,64})$/)
    if (!match) return null
    return { query: match[1], start: cursor - match[1].length - 1, end: cursor }
  }

  render() {
    this.menu.replaceChildren()
    if (!this.results.length) return this.close()
    this.results.forEach((item, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.setAttribute("role", "option")
      button.setAttribute("aria-selected", index === this.activeIndex ? "true" : "false")
      button.className = `flex w-full items-center justify-between px-3 py-2 text-left text-sm ${index === this.activeIndex ? "bg-mod-warm-100 dark:bg-white/10" : "hover:bg-mod-warm-50 dark:hover:bg-white/5"}`
      const kind = item.type === "user" ? "Person" : item.type === "task" ? "Task" : "Project"
      button.innerHTML = `<span class="min-w-0"><span class="block truncate">${this.escape(item.title)}</span><span class="block text-xs text-mod-text-muted">${this.escape(item.subtitle || kind)}</span></span><span class="ml-3 text-xs font-medium text-mod-text-muted">${kind}</span>`
      button.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.choose(item)
      })
      this.menu.appendChild(button)
    })
    this.menu.hidden = false
  }

  close() {
    this.results = []
    this.activeIndex = -1
    if (this.menu) this.menu.hidden = true
  }

  escape(value) {
    const span = document.createElement("span")
    span.textContent = value || ""
    return span.innerHTML
  }
}
